<script setup lang="ts">
import { computed } from 'vue';

import SkeletonLoader from '@/shared/components/UI/SkeletonLoader.vue';
import { useCartShipping } from '@/shared/composables/cart/useCartShipping';
import { useGiftAvailability } from '@/shared/composables/cart/useGiftAvailability';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { formattedPrice } from '@/shared/utils/shopify/money-formatter';

import ProgressBar from './ProgressBar.vue';

type Props = {
  shippingThreshold: number; // in cents
  giftThreshold: number; // in cents
  scenario: 'none' | 'shipping_only' | 'dual_threshold';
  giftCollectionHandle?: string; // gift collection handle (market-resolved in Liquid)
  displayDivider?: boolean;
  initialShowThreshold?: number; // 1 when Liquid detected physical products
  design?: 'rounded' | '';
};

const props = withDefaults(defineProps<Props>(), {
  giftCollectionHandle: '',
  initialShowThreshold: 0,
  design: '',
  displayDivider: false
});

const { t } = useTranslations();
const cartStore = useCartStore();
const { shippingSubtotal, hasPhysicalProducts, totalCartLevelDiscounts } =
  useCartShipping();

// Market-aware gift availability. Only relevant when the configured scenario is
// dual_threshold; for other scenarios we don't fetch (empty handle disables the query).
const { hasAvailableGifts } = useGiftAvailability(
  props.scenario === 'dual_threshold' ? props.giftCollectionHandle : ''
);

// Effective scenario: a dual_threshold cart is downgraded to shipping_only whenever
// the buyer's market has no purchasable gift (empty/out-of-stock collection) OR while
// availability is still resolving. We never advertise a gift we can't fulfil; we only
// upgrade to the gift tier once availability is confirmed.
const effectiveScenario = computed<Props['scenario']>(() => {
  if (props.scenario === 'dual_threshold' && !hasAvailableGifts.value) {
    return 'shipping_only';
  }
  return props.scenario;
});

const effectiveSubtotal = computed(
  () => shippingSubtotal.value - totalCartLevelDiscounts.value
);

// Calculate progress percentages
const shippingProgress = computed(() => {
  if (props.shippingThreshold <= 0) {
    return 0;
  }
  return Math.max(
    0,
    Math.min((effectiveSubtotal.value / props.shippingThreshold) * 100, 100)
  );
});

const giftProgress = computed(() => {
  if (props.giftThreshold <= 0) {
    return 0;
  }
  return Math.max(
    0,
    Math.min((effectiveSubtotal.value / props.giftThreshold) * 100, 100)
  );
});

// Check if thresholds are achieved
const shippingAchieved = computed(() => shippingProgress.value >= 100);

// Calculate remaining amounts
const remainingShipping = computed(() => {
  const remaining = props.shippingThreshold - effectiveSubtotal.value;
  return remaining > 0 ? remaining : 0;
});

const remainingGift = computed(() => {
  const remaining = props.giftThreshold - effectiveSubtotal.value;
  return remaining > 0 ? remaining : 0;
});

// Format remaining amounts
const formattedRemainingShipping = computed(() =>
  formattedPrice(remainingShipping.value)
);
const formattedRemainingGift = computed(() =>
  formattedPrice(remainingGift.value)
);

// Determine messages based on scenario
const shippingMessage = computed(() => {
  if (shippingAchieved.value) {
    return t('shipping.you_earned_free_shipping_message_html');
  }
  return t('shipping.free_shipping_spend_more_tip_html', {
    amount: formattedRemainingShipping.value
  });
});

const dualThresholdMessage = computed(() => {
  if (shippingProgress.value < 100) {
    return shippingMessage.value;
  }

  if (giftProgress.value < 100) {
    return t('free_gift.spend_more_tip_html', {
      amount: formattedRemainingGift.value
    });
  }

  return t('free_gift.you_earned_free_gift_message_html');
});

// Calculate progress for dual mode progress bar
// In dual mode: shipping dot at 50%, gift dot at 100%
const dualModeProgress = computed(() => {
  if (effectiveSubtotal.value <= props.shippingThreshold) {
    // Progress from 0% to 50% based on shipping threshold
    return Math.max(
      0,
      (effectiveSubtotal.value / props.shippingThreshold) * 50
    );
  } else if (effectiveSubtotal.value <= props.giftThreshold) {
    // Progress from 50% to 100% based on gift threshold
    const progressBetweenThresholds =
      ((effectiveSubtotal.value - props.shippingThreshold) /
        (props.giftThreshold - props.shippingThreshold)) *
      50;
    return 50 + progressBetweenThresholds;
  } else {
    // Above gift threshold, show 100%
    return 100;
  }
});

// Design class
const designClass = computed(() => {
  return props.design === 'rounded' ? 'rounded-md md:rounded-lg' : '';
});

// Should show free shipping achieved message
const shouldShowFreeShippingAchieved = computed(() => {
  if (isShippingOnly.value && shippingAchieved.value) {
    return true;
  }
  return false;
});

const shouldShowVisualizer = computed(() => {
  if (cartStore.cart) return hasPhysicalProducts.value;
  return props.initialShowThreshold === 1;
});

const isDualMode = computed(() => effectiveScenario.value === 'dual_threshold');
const isShippingOnly = computed(
  () => effectiveScenario.value === 'shipping_only'
);

const showSkeleton = computed(
  () => shouldShowVisualizer.value && cartStore.cart === null
);
</script>

<template>
  <div v-if="shouldShowVisualizer">
    <div
      v-if="showSkeleton"
      :class="['js-threshold-visualizer', designClass]"
      aria-hidden="true"
    >
      <div class="flex flex-col items-center gap-2 p-4 md:px-8">
        <SkeletonLoader type="text-base" class="h-4 w-3/4 max-w-xs rounded" />
        <SkeletonLoader type="image" class="mt-2 h-1.5 w-full rounded-md" />
      </div>
    </div>

    <div v-else>
      <!-- Free shipping achieved message (for shipping-only scenario) -->
      <div
        v-if="shouldShowFreeShippingAchieved"
        :class="['bg-green-100', designClass]"
      >
        <div class="p-4 text-center text-sm text-green-900">
          <!-- eslint-disable vue/no-v-html -->
          <div v-html="shippingMessage"></div>
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>

      <!-- Threshold visualizer -->
      <div
        v-else
        :class="[
          'js-threshold-visualizer',
          designClass,
          { 'dual-mode': isDualMode }
        ]"
      >
        <!-- Shipping-only block -->
        <div
          v-if="isShippingOnly"
          class="js-shipping-block cart-threshold-mode"
        >
          <div class="flex flex-col items-center gap-2 p-4 md:px-8">
            <span class="text-center text-sm text-gray-darkest">
              <!-- eslint-disable vue/no-v-html -->
              <span v-html="shippingMessage"></span>
              <!-- eslint-enable vue/no-v-html -->
            </span>
            <ProgressBar
              :shipping-progress="shippingProgress"
              :current-amount="effectiveSubtotal"
            />
          </div>
        </div>

        <!-- Dual threshold block (rewards + shipping) -->
        <div
          v-else-if="isDualMode"
          class="js-rewards-block cart-threshold-mode"
        >
          <div class="flex flex-col items-center gap-1 px-4 pb-2 pt-3">
            <span class="text-center text-sm text-gray-darkest">
              <!-- eslint-disable vue/no-v-html -->
              <span v-html="dualThresholdMessage"></span>
              <!-- eslint-enable vue/no-v-html -->
            </span>
            <ProgressBar
              :shipping-threshold="shippingThreshold"
              :gift-threshold="giftThreshold"
              :shipping-progress="shippingProgress"
              :gift-progress="giftProgress"
              :current-amount="effectiveSubtotal"
              :dual-mode-progress="dualModeProgress"
            />
          </div>
        </div>
      </div>
    </div>

    <hr v-if="displayDivider" class="h-2 w-full bg-gray-lightest" />
  </div>
</template>
